import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Markdown } from '@shared/components/typography/Markdown/Markdown';
import { ChannelLogo } from '@shared/components/display/ChannelLogo/ChannelLogo';
import { QuantityCounter } from '@shared/components/inputs/QuantityCounter/QuantityCounter';
import type { EquipmentCardProps } from '@components/ProductOffers/configuration/cards/EquipmentCard/EquipmentCard.types';
import { DiscountBadge } from '@shared/components/display/DiscountBadge/DiscountBadge';
import { SelectableCard } from '@shared/components/SelectableCard';

export const EquipmentCard = ({ equipment, isSelected, quantity, onSelect, onQuantityChange }: EquipmentCardProps) => {
  const isFree = equipment.price === 0;
  const priceLabel = isFree ? 'Free' : `$${equipment.price} / month`;

  return (
    <SelectableCard selectionMode="single" selected={isSelected} onSelect={onSelect}>
      {(selectable) => (
        <Card variant="equipmentCard" selected={isSelected} elevation={0} {...selectable}>
          <Stack className="EquipmentCard-image">
            <ChannelLogo src={equipment.imageUrl ?? ''} name={equipment.name} />
          </Stack>

          <Stack className="EquipmentCard-details" gap="8px">
            <Typography variant="title.card">{equipment.name}</Typography>
            <Box className="EquipmentCard-priceRow">
              <Typography variant="price.card" className="EquipmentCard-price">
                {priceLabel}
              </Typography>
            </Box>
            {equipment.discountTag && (
              <DiscountBadge>
                <Typography variant="discount.tag" className="EquipmentCard-price">
                  {equipment.discountTag}
                </Typography>
              </DiscountBadge>
            )}
            {equipment.discountMessage && (
              <Typography variant="discount.text" className="EquipmentCard-message">
                {equipment.discountMessage}. Discount applied at checkout
              </Typography>
            )}
            <Markdown text={equipment.description} />
          </Stack>

          {equipment.supportsQuantity && !isFree && (
            <Stack className="EquipmentCard-quantity" gap="5px">
              <Stack direction="row" alignItems="center" gap="10px">
                <Typography variant="label.fieldSmall" className="EquipmentCard-quantityLabel">
                  Quantity:
                </Typography>
                <QuantityCounter value={Number(quantity)} min={1} max={10} onChange={onQuantityChange} />
              </Stack>
              <Typography variant="body.sm" className="EquipmentCard-message">
                {`Each quantity adds ${priceLabel}`}
              </Typography>
            </Stack>
          )}
        </Card>
      )}
    </SelectableCard>
  );
};
